import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import BackButton from "@/components/BackButton";
import { Plus, X, Users, Copy, Check, Trash2, KeyRound, Power, Infinity as InfinityIcon, Pencil } from "lucide-react";

const QUOTA_PRESETS = [
  { v: 50, label: "50" },
  { v: 100, label: "100" },
  { v: 250, label: "250" },
  { v: 500, label: "500" },
  { v: "custom", label: "Custom" },
  { v: "unlimited", label: "Unlimited" },
];

const field =
  "w-full bg-[#0B1220] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30";

function QuotaPicker({ preset, custom, onPreset, onCustom, testPrefix }) {
  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-quota-picker`}>
        {QUOTA_PRESETS.map((p) => (
          <button
            key={p.v}
            type="button"
            data-testid={`${testPrefix}-quota-${p.v}`}
            onClick={() => onPreset(p.v)}
            className={`px-3 py-2 rounded-lg text-xs font-mono border transition-colors ${
              preset === p.v ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-white/25"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === "custom" && (
        <input
          type="number"
          min="1"
          data-testid={`${testPrefix}-quota-custom-input`}
          className={`${field} mt-3`}
          placeholder="e.g. 75"
          value={custom}
          onChange={(e) => onCustom(e.target.value)}
        />
      )}
    </div>
  );
}

function resolveQuota(preset, custom) {
  if (preset === "unlimited") return { token_quota: null, unlimited: true };
  if (preset === "custom") return { token_quota: parseInt(custom || "0", 10) || 0 };
  return { token_quota: preset };
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // client being edited
  const [form, setForm] = useState({ username: "", password: "", name: "", company: "", preset: 50, custom: "" });
  const [edit, setEdit] = useState({ preset: 50, custom: "", password: "" });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const load = useCallback(() => api.get("/clients").then(({ data }) => setClients(data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);

  const copy = (t, k) => {
    navigator.clipboard.writeText(t);
    setCopied(k);
    setTimeout(() => setCopied(""), 1500);
  };

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, password: p }));
  };

  const submitCreate = async () => {
    setError("");
    try {
      const q = resolveQuota(form.preset, form.custom);
      const { data } = await api.post("/clients", {
        username: form.username,
        password: form.password,
        name: form.name,
        company: form.company,
        token_quota: q.token_quota,
      });
      setCreated(data);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const closeCreate = () => {
    setOpen(false);
    setCreated(null);
    setError("");
    setForm({ username: "", password: "", name: "", company: "", preset: 50, custom: "" });
  };

  const openEdit = (c) => {
    setEditing(c);
    setEdit({
      preset: c.token_quota == null ? "unlimited" : [50, 100, 250, 500].includes(c.token_quota) ? c.token_quota : "custom",
      custom: c.token_quota == null ? "" : String(c.token_quota),
      password: "",
    });
    setError("");
  };

  const submitEdit = async () => {
    setError("");
    try {
      const q = resolveQuota(edit.preset, edit.custom);
      const body = { ...q };
      if (edit.password) body.password = edit.password;
      await api.patch(`/clients/${editing.id}`, body);
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (c) => {
    await api.patch(`/clients/${c.id}`, { active: !c.active });
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.username}"? Their tokens and scans remain but they can no longer sign in.`)) return;
    await api.delete(`/clients/${c.id}`);
    load();
  };

  return (
    <div>
      <BackButton />
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">CLIENT MANAGEMENT</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Clients</h1>
          <p className="text-slate-400 text-sm mt-2">
            Create client logins with a custom username/password and a scan-token quota. Clients only see their own tokens and scans.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="create-client-button"
          className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg px-4 py-2.5"
        >
          <Plus className="w-4 h-4" /> New Client
        </button>
      </div>

      <div className="card-soc overflow-hidden">
        {clients.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">No clients yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="text-left eyebrow border-b border-white/8">
                <th className="px-6 py-3 font-normal">Client</th>
                <th className="px-6 py-3 font-normal">Username</th>
                <th className="px-6 py-3 font-normal">Token Quota</th>
                <th className="px-6 py-3 font-normal">Scans</th>
                <th className="px-6 py-3 font-normal">Status</th>
                <th className="px-6 py-3 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const pct = c.token_quota ? Math.min(100, (c.tokens_generated / c.token_quota) * 100) : 0;
                return (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-6 py-3.5">
                      <div className="text-slate-100 font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.company || "—"}</div>
                    </td>
                    <td className="px-6 py-3.5 font-mono text-xs text-cyan-300">{c.username}</td>
                    <td className="px-6 py-3.5">
                      <div className="font-mono text-xs text-slate-200">
                        {c.token_quota == null ? (
                          <span className="flex items-center gap-1">{c.tokens_generated} used · <InfinityIcon className="w-3.5 h-3.5" /> unlimited</span>
                        ) : (
                          `${c.tokens_generated} / ${c.token_quota}`
                        )}
                      </div>
                      {c.token_quota != null && (
                        <div className="h-1.5 w-32 bg-white/5 rounded mt-1.5 overflow-hidden">
                          <div className={`h-full ${pct >= 100 ? "bg-red-400" : pct >= 80 ? "bg-amber-400" : "bg-cyan-400"}`} style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-slate-300">{c.scan_count}</td>
                    <td className="px-6 py-3.5">
                      <span
                        className="font-mono text-[10px] font-semibold px-2 py-1 rounded tracking-wider"
                        style={c.active ? { color: "#10B981", background: "rgba(16,185,129,0.12)" } : { color: "#64748B", background: "rgba(100,116,139,0.15)" }}
                      >
                        {c.active ? "ACTIVE" : "DISABLED"}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <button title="Edit quota / reset password" data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button title={c.active ? "Disable" : "Enable"} data-testid={`toggle-client-${c.id}`} onClick={() => toggleActive(c)} className={`p-2 rounded-lg bg-white/5 hover:bg-white/10 ${c.active ? "text-amber-300" : "text-emerald-300"}`}>
                          <Power className="w-4 h-4" />
                        </button>
                        <button title="Delete" data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} className="p-2 rounded-lg bg-white/5 hover:bg-red-500/15 text-slate-400 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeCreate} />
          <div className="relative card-soc w-full max-w-lg glow-blue fade-up max-h-[90vh] overflow-y-auto" data-testid="create-client-modal">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <div className="flex items-center gap-2.5">
                <Users className="w-5 h-5 text-cyan-400" />
                <h3 className="font-head font-bold text-lg">{created ? "Client Created" : "New Client Account"}</h3>
              </div>
              <button onClick={closeCreate} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            {!created ? (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="eyebrow block mb-1.5">Username *</label>
                    <input data-testid="client-username-input" className={field} placeholder="acme-hr" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                  </div>
                  <div>
                    <label className="eyebrow block mb-1.5">Password *</label>
                    <div className="flex gap-2">
                      <input data-testid="client-password-input" className={field} placeholder="min 6 chars" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                      <button type="button" onClick={genPassword} title="Generate" className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><KeyRound className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="eyebrow block mb-1.5">Display Name</label>
                    <input data-testid="client-name-input" className={field} placeholder="Acme HR Team" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="eyebrow block mb-1.5">Company</label>
                    <input data-testid="client-company-input" className={field} placeholder="Acme Corp" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Scan Token Quota</label>
                  <QuotaPicker preset={form.preset} custom={form.custom} onPreset={(v) => setForm({ ...form, preset: v })} onCustom={(v) => setForm({ ...form, custom: v })} testPrefix="create" />
                </div>
                {error && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2" data-testid="client-error">{error}</div>}
                <button onClick={submitCreate} data-testid="submit-create-client" className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg py-3">Create Client</button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <p className="text-sm text-slate-400">Share these credentials with the client once — the password is not shown again.</p>
                {[["Username", created.username, "u"], ["Password", created.initial_password, "p"]].map(([l, v, k]) => (
                  <div key={k}>
                    <label className="eyebrow block mb-1.5">{l}</label>
                    <div className="flex items-center gap-2">
                      <code className="term flex-1 px-3 py-2.5 text-cyan-300 text-sm" data-testid={`created-client-${k}`}>{v}</code>
                      <button onClick={() => copy(v, k)} className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300">
                        {copied === k ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
                <div>
                  <label className="eyebrow block mb-1.5">Login URL</label>
                  <code className="term block px-3 py-2.5 text-slate-300 text-xs">{window.location.origin}/login</code>
                </div>
                <div className="text-xs text-slate-500">Quota: {created.token_quota == null ? "unlimited" : `${created.token_quota} scan tokens`}</div>
                <button onClick={closeCreate} className="w-full border border-white/10 hover:bg-white/5 text-slate-300 rounded-lg py-2.5 text-sm">Done</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative card-soc w-full max-w-md glow-blue fade-up" data-testid="edit-client-modal">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <h3 className="font-head font-bold text-lg">Edit · {editing.username}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="eyebrow block mb-1.5">Scan Token Quota (used: {editing.tokens_generated})</label>
                <QuotaPicker preset={edit.preset} custom={edit.custom} onPreset={(v) => setEdit({ ...edit, preset: v })} onCustom={(v) => setEdit({ ...edit, custom: v })} testPrefix="edit" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Reset Password (optional)</label>
                <input data-testid="edit-client-password" className={field} placeholder="leave blank to keep" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} />
              </div>
              {error && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>}
              <button onClick={submitEdit} data-testid="submit-edit-client" className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg py-3">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
